import { FC, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import Swiper from 'swiper';
import { Pagination } from 'swiper';
import Slider from 'components/commercetools-ui/atoms/slider';
import useClassNames from 'helpers/hooks/useClassNames';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, tablet } from 'helpers/utils/screensizes';
import Image from 'frontastic/lib/image';
import { ProductDetailsProps } from '../product/product-details';
import debounce from 'lodash.debounce';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import { useFormat } from 'helpers/hooks/useFormat';
import Modal from 'components/commercetools-ui/atoms/modal';
// @ts-ignore
import ImageZoom from "react-image-zooom";

declare global {
  interface Window {
    ConfigureUI?: any;
  }
}

interface GalleryProps {
  images: Array<string>;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  setLoading: (value: boolean) => void;
  sku: string;
  color: string;
}

const Gallery: FC<GalleryProps> = ({ images, inModalVersion, setLoading, sku, color }) => {
  const swiperRef = useRef<Swiper>();
  const [activeSlide, setActiveSlide] = useState(0);
  const width = useWindowWidth();
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isPopup, setIsPopup] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isImageError, setIsImageError] = useState(true);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const imagesToDisplay = images.length === 1 ? [...images, ...images] : images;
  const [transitionDuration, setTransitionDuration] = useState(1000);

  const slideTo = (slide: number) => {
    swiperRef.current?.slideTo(slide);
  };

  const closeModal = useCallback(() => setIsPopup(false), []);
  function rotate(A: any, n: number, l = A.length) {
    const offset = ((n % l) + l) % l;
    return A.slice(offset).concat(A.slice(0, offset));
  }

  const reversedArray = rotate(imagesToDisplay, currentImage);

  const clearLoading = useCallback(
    debounce(() => setLoading(false), 2000),
    [setLoading],
  );

  useEffect(() => {
    clearLoading();
  }, [clearLoading]);

  const slideToActive = (slideIndex: number) => {
    swiperRef.current?.slideTo(slideIndex);
  };

  useEffect(() => {
    slideToActive(activeSlide);
  }, [activeSlide]);

  const reinitializeConfigureUI = useCallback(() => {
    if (isConfigured && swiperRef.current) {
      const applyToAll = images.length === 1;
      swiperRef.current.slides.forEach((slide, index) => {
        if (applyToAll || index % 2 === 0) {
          new window.ConfigureUI({
            id: '64180e9626eee11f02dac5b7',
            mode: 'lightbox',
            buttonPosition: 'inline',
            sku: `${sku}*${color}`,
            element: slide,
          });
        }
      });
    }
  }, [isConfigured, images, sku, color]);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.slides) {
      swiperRef.current.slides.forEach((slide) => {
        const existingButton = slide.querySelector('#configure-button');
        if (existingButton) {
          existingButton.remove();
        }
      });
      reinitializeConfigureUI();
    }
  }, [reinitializeConfigureUI, sku, color, images.length]);

  const imagesContainerClassName = useClassNames([
    'relative ml-20 lg:mx-auto',
    inModalVersion ? 'h-[250px] max-w-[300px]' : 'h-full',
  ]);

  const clearImage = useCallback(
    debounce(() => setLoading(false), 750),
    [setLoading],
  );

  useEffect(() => {
    clearImage();
  }, [clearImage]);

  return (
    <div className="w-full gap-y-34 px-24 md:px-28 lg:px-28 xl:px-30 2xl:px-30">
      {imagesToDisplay.length > 0 ? (
        <Slider
          arrows={isTabletSize ? true : false}
          dots={isTabletSize ? false : true}
          loop
          speed={transitionDuration}
          prevButtonStyles={{
            left: isDesktopSize ? -30 : -10,
            padding: isDesktopSize ? '5px' : '10px',
            zIndex: 10,
          }}
          nextButtonStyles={{
            right: isDesktopSize ? -30 : -10,
            padding: isDesktopSize ? '5px' : '10px',
            zIndex: 10,
          }}
          slidesPerView={isTabletSize ? 2 : 1.25}
          spaceBetween={8}
          allowArrowsOnTouchDevice={true}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {imagesToDisplay.map((image, index) => (
            <div
              key={index}
              className={`relative ${index === activeSlide ? 'ps-slider-for' : ''}`}
            >
              <div
                className="my-360-container hover:cursor-pointer"
                onClick={() => {
                  setIsPopup(!isPopup);
                  setCurrentImage(index);
                }}
              >
                <Image
                  priority
                  src={encodeURI(image)}
                  alt={image}
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '650px',
                    objectFit: 'cover',
                  }}
                  width={500}
                  height={300}
                  onLoad={() => setLoading(false)}
                  onError={(event: any) => {
                    event.target.src = '/images/no-image-placeholder/product-thumbnail.jpg';
                    event.target.srcset = '/images/no-image-placeholder/product-thumbnail.jpg';
                  }}
                />
              </div>
            </div>
          ))}
        </Slider>
      ) : (
        <Image
          priority
          src={'/images/no-image-placeholder/product-thumbnail.jpg'}
          alt={'No Image found'}
          sizes="100vw"
          style={{
            width: '100%',
            height: 'auto',
            maxWidth: '390px',
            objectFit: 'contain',
          }}
          width={500}
          height={300}
          onLoad={() => setLoading(false)}
        />
      )}
      {isPopup && (
        <Modal
        isOpen={isPopup}
        onRequestClose={closeModal}
        style={{ content: { backgroundColor: 'white' }, overlay: { backgroundColor: 'rgba(0 ,0, 0, .75)' } }}
        className="relative border-transparent focus:border-transparent focus:outline-none focus:ring-0"
        closeButton
      >
        <Slider
          arrows={isTabletSize ? true : false}
          dots={false}
          fractionPagination
          loop
          autoplay={false}
          prevButtonStyles={{
            left: isDesktopSize ? -75 : -50,
            padding: isDesktopSize ? '5px' : '10px',
            zIndex: 10,
          }}
          nextButtonStyles={{
            right: isDesktopSize ? -75 : -50,
            padding: isDesktopSize ? '5px' : '10px',
            zIndex: 10,
            color: 'white',
          }}
          prevArrowStyles={{ border: 'solid #eee', borderWidth: '0 2px 2px 0' }}
          nextArrowStyles={{ border: 'solid #eee', borderWidth: '0 2px 2px 0' }}
          slidesPerView={1}
          allowArrowsOnTouchDevice={true}
          speed={transitionDuration}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {reversedArray.map((image: string, index: number) => (
            <div key={index} className="w-full h-full relative">
              <ImageZoom
                src={encodeURI(image)}
                alt={image}                
                zoom={200}
                className="!w-full !h-full !object-cover !block"
                onError={(event: any) => {
                  event.target.src = '/images/no-image-placeholder/product-thumbnail.jpg';
                }}
              />
            </div>
          ))}
        </Slider>
      </Modal>
      )}      
    </div>
  );
};

export default Gallery;
